/**
 * The `teaser` block: what the visitor sees beside the closed launcher.
 *
 * Vendored from web-client, which is the source of truth (`src/ts/index.ts`,
 * mirrored in its published `web-client.d.ts`). Channels passes the integration
 * config through untouched, so what tooling writes here is byte-for-byte what
 * the widget reads. Keep this file in step with web-client when either moves.
 */

/** Which invitation the closed launcher shows. */
export type TeaserVariant = 'callout' | 'question_chips' | 'composer' | 'none';

/**
 * One member per shape, each declaring only the fields it renders.
 *
 * This is a union, not a flat object, because the orchestrator's Joi schema
 * rejects a field the chosen variant does not own — `placeholder` on a
 * `callout`, `message` on `question_chips`, `quick_replies` on anything but
 * chips, anything at all on `none`. A form that posts a stale field gets a save
 * error rather than a silently ignored key, which is why the variant picker has
 * to own the field set and why `toTeaserBlock` narrows before we send.
 *
 * `variant` is optional on the first two, so replies alone mean chips and their
 * absence means the line: `{ quick_replies: [...] }` is a whole config.
 */
export type TeaserConfig =
  /** The line alone, in the bubble beside the launcher. */
  | { variant?: 'callout' | null; message?: string | null }
  /** Tappable questions in place of the line. At most three render. */
  | { variant?: 'question_chips' | null; quick_replies: string[] }
  /** The line, and an input the visitor types their own question into. */
  | { variant: 'composer'; message?: string | null; placeholder?: string | null }
  /** Launcher only. */
  | { variant: 'none' };

/**
 * One `sections[]` entry: a teaser, plus the part of the page it belongs to.
 *
 * The section with the largest visible share of the viewport wins at runtime, so
 * **the order of this list is presentational only** — do not describe it as
 * "first match wins", which is true of `pages[]` and wrong here.
 */
export type TeaserSection = TeaserConfig & {
  /**
   * Authored, and stable across a reorder — it names the section in analytics,
   * so deriving it from the position would break the history whenever someone
   * moved a row.
   */
  id: string;
  /**
   * CSS selectors, strongest first. The loader walks them in order and observes
   * the first that matches anything; a selector matching several elements
   * resolves to the first in document order. Nothing but a selector is ever
   * tried — no heading text, no CMS awareness.
   *
   * The weaker entries are what carry a section through a customer redeploy, so
   * never silently drop them.
   */
  match: string[];
};

/**
 * One `pages[]` entry: a teaser, plus the URL it belongs to.
 *
 * `url` is a hint, not a pattern — it matches when it appears anywhere in the
 * host page's URL, compared case-insensitively. Nothing is escaped and nothing
 * is compiled, so a stray `.` or `(` cannot change what matches or throw. It is
 * a plain substring, so this is not `page_triggers` and must not reuse its
 * regex validation or its wording.
 *
 * **The order of this list IS the matching semantics** — the first entry whose
 * `url` appears in the host URL wins. Appending is therefore safe; inserting
 * changes which teaser an existing URL resolves to.
 */
export type TeaserPage = TeaserConfig & {
  url: string;
  /**
   * What the author wants said on this page, in their words — "we are pushing
   * the annual plan", "do not mention the free trial", "keep it formal".
   *
   * Authoring-only, and never read by the widget: it is context for the model
   * when it writes this page's sections, so a regenerate reuses the same
   * instructions rather than making the author restate them.
   */
  guidelines?: string | null;
  /**
   * A different teaser per part of this page. No section in view falls back to
   * this entry; no page match falls back to the block.
   */
  sections?: TeaserSection[] | null;
};

export type TeaserBlock = TeaserConfig & {
  /** Checked in order; the first match wins and overrides the block. */
  pages?: TeaserPage[] | null;
  /**
   * The site these pages belong to, e.g. `https://moveo.ai`. Authoring-only.
   *
   * It exists so `pages[].url` can stay a path. A path is the better hint —
   * it survives a domain change, a staging host and `www` vs no-`www` — but a
   * path cannot be fetched, and both the preview and the section generator
   * need something concrete to open in a browser. This supplies the missing
   * half: `website_url + url` is what gets rendered, while `url` alone is what
   * the widget matches.
   *
   * That split is also what makes section teasers testable in the preview. The
   * widget matches against the *host document's* location, which in a preview
   * is tooling's own URL — `https://moveo.ai/pricing` can never appear there,
   * but `/pricing` can, and the preview route mirrors it into its path.
   *
   * The widget never reads this. It is carried inside `teaser` to keep the
   * feature in one place, and channels unmarshals the block into a typed struct
   * so it is dropped before reaching a visitor.
   */
  website_url?: string | null;
  /**
   * Seconds to wait before the teaser appears, counted from the moment it has
   * something to say. Absent or `0` shows it straight away. A positive value is
   * clamped to `TEASER_SHOW_AFTER_MIN`–`TEASER_SHOW_AFTER_MAX`.
   *
   * Block-level only: a per-page clock restarts on every SPA navigation, so the
   * teaser would arrive later on each page and, past a few, never at all.
   */
  show_after?: number | null;
  /**
   * Seconds the teaser stays before it retires for the rest of the visit.
   * Absent or `0` keeps it up. A positive value is clamped to
   * `TEASER_HIDE_AFTER_MIN`–`TEASER_HIDE_AFTER_MAX`. Block-level only, for the
   * same reason as `show_after`.
   */
  hide_after?: number | null;
};

/**
 * Three is what the design offers, and the orchestrator rejects a fourth. The
 * widget also slices to three, so a fourth would be silently dropped rather
 * than shown — surface the cap in the form instead.
 */
export const TEASER_MAX_QUICK_REPLIES = 3;

/**
 * Cap on a page's `guidelines`.
 *
 * It rides in the generator's prompt, so it is bounded for the same reason
 * every other prompt input is: a page that carries an essay costs tokens on
 * every regenerate and crowds out the page's own markup. Generous enough for
 * real instructions, small enough that it cannot dominate the prompt.
 */
export const PAGE_GUIDELINES_MAX = 1000;

/**
 * Clamp bounds, mirroring web-client's `src/config/constants.ts`.
 *
 * These bound a *positive* value. Empty and `0` both mean "off" and must reach
 * the wire as absent — never coerce a cleared field up to the minimum.
 */
export const TEASER_SHOW_AFTER_MIN = 1;
export const TEASER_SHOW_AFTER_MAX = 60;
export const TEASER_HIDE_AFTER_MIN = 5;
export const TEASER_HIDE_AFTER_MAX = 300;
